// base
import { createSlice } from '@reduxjs/toolkit';
import { SAFETY_SLICE_NAME } from '../actions/safety.action';

// thunks
import {
  fetchSafetyTrainings,
  fetchSafetyTrainingDetail,
  createSafety,
  updateSafety,
  updateSafetyStatus,
  deleteSafetyAttendance,
  createSafetyByManual,
  fetchSafetySystems,
  deleteSafety,
  createSafetySetting,
  updateSafetySetting,
  deleteSafetySetting,
  deleteAllSafetyAttendance
} from '../thunks/safety.thunk';
import {
  ISafetyDetail,
  ISafetyMeta,
  ISafetySetting,
  ReqSafeties,
  ReqSafetyDetail,
  ReqSafetySettings
} from '../model/safety.model';
import { TotalMapProps } from 'modules/common';
import dayjs from 'dayjs';
import { dateToString } from 'utils';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'constants/const';
import { IOption } from 'modules/nation/models/nation.model';
import { IDialog, IDialogType, IPageAutority } from 'modules/notice/models/notice.model';

export interface SafetyState {
  safetyMetas: ISafetyMeta[];
  safetyMetasTotalMap: TotalMapProps;

  safetyDetail: ISafetyDetail;
  safetyDetailTotalMap: TotalMapProps;

  safetySystems: ISafetySetting[];
  safetyOptions: (IOption & ISafetySetting)[];
  safetyAllOptions: (IOption & ISafetySetting)[];
  safetySystemDetail: ISafetySetting;
  safetySubSystemDetail: ISafetySetting;

  fetchSafetyTrainingRequest: ReqSafeties;
  fetchSafetyDetailRequest: ReqSafetyDetail;

  fetchSafeteySettingRequest: ReqSafetySettings;

  dialog: IDialog;
  pageAuthority: IPageAutority;
}

// initialState
const initialState: SafetyState = {
  safetyMetas: [],
  safetyMetasTotalMap: {
    totalElements: 0,
    totalPages: 0
  },

  safetyDetail: {
    safetyId: '',
    categoryId: '',
    categoryName: '',
    trainingId: '',
    trainingName: '',
    trainerId: '',
    trainerName: '',
    contents: '',
    trainingDate: dateToString(dayjs().toDate()),
    status: '',
    fileId: undefined,
    fileUrl: undefined,
    file: undefined,
    attendances: {
      content: [],
      totalElements: 0,
      totalPages: 0
    }
  },
  safetyDetailTotalMap: {
    totalElements: 0,
    totalPages: 0
  },

  safetySystems: [],
  safetyOptions: [],
  safetyAllOptions: [],

  safetySystemDetail: {
    systemId: '',
    clsCd: 0,
    clsNm: '',
    cdNo: 0,
    cdNm: '',
    optCd1: 0,
    optCd2: ''
  },
  safetySubSystemDetail: {
    systemId: '',
    clsCd: 0,
    clsNm: '',
    cdNo: 0,
    cdNm: '',
    optCd1: 0,
    optCd2: ''
  },

  fetchSafetyTrainingRequest: {
    startDate: dateToString(dayjs().subtract(1, 'year').toDate()),
    endDate: dateToString(dayjs().toDate()),
    categoryId: 'ALL',
    keyword: '',
    page: DEFAULT_PAGE,
    size: DEFAULT_PAGE_SIZE
  },
  fetchSafetyDetailRequest: {
    keyword: '',
    page: DEFAULT_PAGE,
    size: DEFAULT_PAGE_SIZE
  },
  fetchSafeteySettingRequest: {
    page: DEFAULT_PAGE,
    size: DEFAULT_PAGE_SIZE
  },
  dialog: {
    type: IDialogType.none,
    message: undefined,
    isNavigate: false,
    callback: () => {}
  },
  pageAuthority: {
    isReadonly: false,
    hasAuthority: false
  }
};

// createSlice
const safetySlice = createSlice({
  name: SAFETY_SLICE_NAME,
  initialState,
  reducers: {
    changeSafetyDetail: (state, action) => {
      state.safetyDetail = {
        ...state.safetyDetail,
        ...action.payload
      };
    },

    changeFetchSafetyTrainingRequest: (state, action) => {
      state.fetchSafetyTrainingRequest = {
        ...state.fetchSafetyTrainingRequest,
        ...action.payload
      };
    },

    changeFetchSafetyDetailRequest: (state, action) => {
      state.fetchSafetyDetailRequest = {
        ...state.fetchSafetyDetailRequest,
        ...action.payload
      };
    },
    resetFetchSafetyDetailRequest: (state) => {
      state.fetchSafetyDetailRequest = initialState.fetchSafetyDetailRequest;
    },

    changeSafetySystemDetail: (state, action) => {
      state.safetySystemDetail = {
        ...state.safetySystemDetail,
        ...action.payload
      };
    },
    changeSafetySubSystemDetail: (state, action) => {
      state.safetySubSystemDetail = {
        ...state.safetySubSystemDetail,
        ...action.payload
      };
    },
    resetSafetySystemDetail: (state) => {
      state.safetySystemDetail = initialState.safetySystemDetail;
    },
    resetSafetySubSystemDetail: (state) => {
      state.safetySubSystemDetail = initialState.safetySubSystemDetail;
    },

    resetSafetyDetail: (state) => {
      state.safetyDetail = initialState.safetyDetail;
    },

    changeDialog: (state, action) => {
      state.dialog = {
        ...state.dialog,
        ...action.payload
      };
    },
    resetDialog: (state) => {
      state.dialog = initialState.dialog;
    },

    changePageAuthority: (state, action) => {
      state.pageAuthority = {
        ...state.pageAuthority,
        ...action.payload
      };
    },
    resetAll: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSafetyTrainings.fulfilled, (state, action) => {
        state.safetyMetas = action.payload.resultList;
        state.safetyMetasTotalMap = action.payload.totalMap;
      })

      .addCase(fetchSafetyTrainingDetail.fulfilled, (state, action) => {
        const detail = action.payload.resultObject;

        state.safetyDetail = {
          ...detail,
          fileUrl: `${import.meta.env.VITE_API_URL}${detail.fileUrl}`
        };

        state.safetyDetailTotalMap = {
          totalElements: detail.attendances.totalElements,
          totalPages: detail.attendances.totalElements
        };
      })

      .addCase(createSafety.fulfilled, (state, action) => {
        state.dialog.isNavigate = true;
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })
      .addCase(createSafety.rejected, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })

      .addCase(updateSafety.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.isNavigate = true;
        state.dialog.message = action.payload.message;
      })
      .addCase(updateSafety.rejected, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })

      .addCase(deleteSafety.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.isNavigate = true;
        state.dialog.message = action.payload.message;
      })
      .addCase(deleteSafety.rejected, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })

      .addCase(updateSafetyStatus.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })
      .addCase(deleteSafetyAttendance.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })
      .addCase(deleteAllSafetyAttendance.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })
      .addCase(deleteAllSafetyAttendance.rejected, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })

      .addCase(createSafetyByManual.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.isNavigate = true;
        state.dialog.message = action.payload.message;
      })

      .addCase(fetchSafetySystems.fulfilled, (state, action) => {
        const systems = action.payload.resultList;

        state.safetySystems = systems;
        state.safetyOptions = systems.map((item) => ({
          ...item,
          label: item.cdNm,
          value: item.systemId
        }));
        state.safetyAllOptions = [
          {
            label: 'ALL',
            value: 'ALL',
            systemId: '',
            clsCd: 0,
            clsNm: '',
            cdNo: 0,
            cdNm: '',
            optCd1: 0,
            optCd2: ''
          },
          ...systems.map((item) => ({
            ...item,
            label: item.cdNm,
            value: item.systemId
          }))
        ];
      })
      .addCase(createSafetySetting.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })
      .addCase(updateSafetySetting.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })
      .addCase(deleteSafetySetting.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      });
  }
});

export const safety = safetySlice.name;
export const safetyReducer = safetySlice.reducer;
export const safetyActions = safetySlice.actions;
export const {
  changeFetchSafetyTrainingRequest,
  changeFetchSafetyDetailRequest,
  changeSafetyDetail,
  changeSafetySystemDetail,
  changeSafetySubSystemDetail,
  resetSafetySystemDetail,
  resetAll,
  resetFetchSafetyDetailRequest,
  resetSafetyDetail,
  resetDialog,
  changeDialog,
  resetSafetySubSystemDetail,
  changePageAuthority
} = safetySlice.actions;

export default safetySlice.reducer;
